import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Users, Search, UserPlus, Edit2, Trash2, User } from "lucide-react";
import { getRoleIcon, getRoleBadge, getScoreColor } from "./adminHelpers";

const STATUS_STYLE = {
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending_approval: "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  suspended: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = {
  approved: "Active", pending_approval: "Pending", rejected: "rejected", suspended: "suspended",
};

function getStatusBadge(status) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.approved;
  const label = STATUS_LABEL[status] || "Active";
  return { style, label };
}

export function UserManagement({
  filteredUsers, searchQuery, setSearchQuery,
  showCreateUser, setShowCreateUser, newUser, setNewUser,
  createError, handleCreateUser, teams,
  handleDeleteUser, setEditingUser, setEditForm, currentUserId
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-slate-200"
            data-testid="user-search-input"
          />
        </div>
        
        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium" data-testid="create-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#0B1F3B]">Create New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {createError && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg" data-testid="create-user-error">{createError}</div>
              )}
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="Rajesh Singh" data-testid="new-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="rajesh@company.com" data-testid="new-user-email" />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Min 6 characters" data-testid="new-user-password" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team (Optional)</Label>
                <Select value={newUser.team_id || "none"} onValueChange={(v) => setNewUser({...newUser, team_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="new-user-team">
                    <SelectValue placeholder="No team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No team</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateUser} className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90" data-testid="submit-create-user">
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card className="premium-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Team</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sessions</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Readiness</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <Link to={`/profile/${u.id}`} className="hover:underline">
                        <p className="font-medium text-[#0B1F3B] text-sm">{u.name}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={`${getRoleBadge(u.role)} text-xs`}>
                        {getRoleIcon(u.role)}
                        <span className="ml-1 capitalize">{u.role}</span>
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const { style, label } = getStatusBadge(u.status);
                        return <Badge className={`text-xs border ${style}`}>{label}</Badge>;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">{u.team_name || "\u2014"}</td>
                    <td className="py-3 px-4 text-center text-sm font-semibold text-[#0B1F3B]">{u.total_sessions}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-bold text-sm ${getScoreColor(u.average_readiness)}`}>
                        {u.average_readiness > 0 ? `${u.average_readiness}%` : "\u2014"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/profile/${u.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#2563EB]">
                            <User className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#F59E0B]"
                          onClick={() => { setEditingUser(u); setEditForm({ name: u.name, role: u.role, team_id: u.team_id || "" }); }}
                          data-testid={`edit-user-${u.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {u.id !== currentUserId && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={() => handleDeleteUser(u.id)}
                            data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No users found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
